import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { history, useIntl, useDispatch, useSelector } from 'umi';
import { Button, Card, Justify, Modal, PagingQuery } from 'tea-component';
import TipContext from '@home/components/TipContextComponents/TipContext';
import Loading from '@intelligence/components/Loading';
import CardSlot from '@intelligence/components/CardSlot';
import SearchRecent from '@intelligence/components/SearchRecent';
import ArrowDownText from '@intelligence/components/ArrowdownText';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import ExportTable from '@intelligence/components/ExportTable';
import ChartIconType from '@intelligence/components/ChartsComponents/ChartIconType';
import ChangedMetricSelect from '@intelligence/v3/components/ChangedMetricSelect';
import Filter, { ICommonFilterInstance } from '@intelligence/pages/TopCharts/components/CommonFilter';
import { buildResetFilterValue } from '@intelligence/pages/TopCharts/components/CommonFilter/resetValue';
import MultiDataSourceTopChartsVisualis from '../MultiDataSourceTopChartsVisualis';
import MultiDataSourceTopChartsTable from '../MultiDataSourceTopChartsTable';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import useTextInfo from '../../_hooks/useTextInfo';
import useOperationState from '../../_hooks/useOperationState';
import useTopChartsCore from '../../_hooks/useTopChartsCore';
import useCheckedGenresClear from '../../_hooks/useCheckedGenresClear';
import { fetchMetricDataGames } from '@intelligence/v3/services/topCharts';
import { fetchMobileTopChartsDataWhenSearchKeyword } from '../../_services';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { getGranularityAUMetricKey } from '@intelligence/v3/utils/metric';
import { generateGenreSelectValueSimple } from '@intelligence/v3/utils/genre';
import { transTableDataForSearch } from '@intelligence/v3/utils/topcharts';
import { buildTopChartsTagClickHandler } from '@intelligence/v3/utils/sensorTowerTagColumns';
import { dbiBtnLogMes, dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import { getValidTopSelectGame } from '@intelligence/utils/topchartsUtils';
import { getFilterName, getFilterValueStorage } from '@intelligence/components/Filter/util';
import { convertParams } from '../../_services/utils';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { mergeAppMagicGenreValue, getSurgeMetricOptions } from './utils';
import { AppMagicGenreTagType } from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';
import {
  URL_SHORT_LINK_NAME,
  URL_CHART_TYPE,
  URL_SORT_NAME,
  URL_ORDER,
  URL_SOURCE_NAME,
  URL_RATIO_TYPE,
  updateShortLinkPath,
} from '@intelligence/utils/urlPathCfg';
import { SortType } from '@home/enums/common';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import { DBI_TOPCHARTS } from '@intelligence/models/topcharts';
import { PAGINATION_FETCH_PARAMS } from '@intelligence/v3/constants';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { MultiDataSourceTopChartsPageType } from '../../_config/pageConfigs';

import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type {
  TableSelectStatusType,
  TableSelectionType,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { ShortLinkQueryType } from '@intelligence/utils/urlPathCfg';

import './index.scss';

const GAME_PRIMARY_KEY = 'game_id';
const AU_METRICS = ['dau', 'wau', 'mau'];
const SHOW_NEW_TEXT_TOP_CHARTS = [
  MultiDataSourceTopChartsPageType.ActiveUsers,
  MultiDataSourceTopChartsPageType.Revenue,
  MultiDataSourceTopChartsPageType.Downloads,
];

export interface IMultiDataSourceTopChartsTemplateNotificationsProps {
  showFirstGuide?: boolean;
  showFirstDownLoadBubble?: boolean;
  setShowFirstDownLoadBubble?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IMultiDataSourceTopChartsTemplateProps
  extends IBaseProps,
  IMultiDataSourceTopChartsTemplateNotificationsProps {
  entityType?: IEntityType;
  isDataBrain?: boolean;
  topChartsType: 'activeuser' | 'revenue' | 'downloads' | 'databrain';
  topchartsNameParams: string;
  pageLogKey: string;
  metrics: IRecord[];
  metric: string;
  filterLoading: boolean;
  filterCfg: TopchartFilterCfgType;
  defaultParams: object;
  // url链接的其他参数组装成的参数
  urlCustomParams: object;
  // 页面操作的有效配置
  shortLinkValidQuery: ShortLinkQueryType;
  // 从短链接通过接口获取的url筛选器
  shortLinkFilterValue: any;
  rangeTimeComponentDefault: any;
  // App Magic 品类标签点击模式：replace(替换，默认) | append(追加)
  appMagicTagClickMode?: 'replace' | 'append';
  /**
   * 禁用筛选器和游戏选择跨Tab继承
   * 当为 true 时，切换Tab不会继承Redux中的筛选器值和游戏选择
   * 默认为 false（保持现有继承行为）
   */
  disableInheritance?: boolean;
  /** 当前 Mobile tab 的筛选器继承作用域。 */
  pageId?: string;
}

const MultiDataSourceTopChartsTemplate: React.FC<IMultiDataSourceTopChartsTemplateProps> = (props) => {
  const {
    style,
    className,
    entityType = 'mobile',
    isDataBrain = false,
    showFirstGuide = false,
    showFirstDownLoadBubble = false,
    setShowFirstDownLoadBubble,
    topChartsType = MultiDataSourceTopChartsPageType.ActiveUsers,
    topchartsNameParams,
    pageLogKey = 'topchartsMobileGamesActiveUsers',
    downloadLogKey = 'topchartMobileGameActiveUserDownLoadClick',
    metrics,
    metric,
    filterLoading,
    filterCfg,
    defaultParams = {},
    urlCustomParams,
    shortLinkValidQuery,
    shortLinkFilterValue,
    rangeTimeComponentDefault,
    appMagicTagClickMode = 'replace',
    genreOptionsMap,
    disableInheritance = false,
    pageId,
  } = props;

  const query = history.location.query || {};
  const hasUrlShortLinkVal = query[URL_SHORT_LINK_NAME];
  const topSelect = query?.topSelect;
  const urlDefaultSelectedGameId = useMemo(() => {
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    return String(query?.studioUnifiedid || searchParams?.get('studioUnifiedid') || '').trim();
  }, [query?.studioUnifiedid, history.location.search]);

  const pageLogInfo = dbiPageLogMes[pageLogKey];
  const defaultValue = filterCfg?.defaultValue;
  const showNewText = SHOW_NEW_TEXT_TOP_CHARTS.includes(topChartsType);

  const { formatMessage, locale } = useIntl();

  const dispatch = useDispatch();

  const { userInfo, activeUserGameList } = useSelector<any, any>((rootState) => {
    const homeState = rootState?.home || {};
    const userInfo = homeState?.userInfo || {};

    const topChartsState = rootState?.[DBI_TOPCHARTS] || {};
    const activeUserGameList = topChartsState?.activeUserGameList || [];

    return { userInfo, activeUserGameList };
  });

  // Active, UsersRevenue, Downloads三个榜单如果游戏有勾选则切换标签时继承
  // 当 disableInheritance 为 true 时，强制忽略游戏继承
  const extendGameList = disableInheritance
    ? []
    : topChartsType === MultiDataSourceTopChartsPageType.DataBrainCalibration
      ? []
      : activeUserGameList;
  // 当前 tab 只读取自己的继承分区；详情页继承仍由 useFilterExtend 内部同步维护。
  const { filterExtend, setFilterExtend, isOperate } = useFilterExtend({ pageId });
  const [isError, setIsError] = useState(false);
  // 异动指标状态
  const [surgeMetric, setSurgeMetric] = useState<string>('');
  // 在筛选器这里拿到缓存的值，并渲染
  const filterSaveName = getFilterName(userInfo);
  const filterSaveData = getFilterValueStorage(userInfo);
  const buildInitialResetBaseValue = (baseValue: any) => {
    return buildResetFilterValue({
      resetBaseValue: baseValue,
      fallbackResetValue: defaultValue,
      filterKeyList: filterCfg?.filterKeyList,
      rangeTimeComponentDefault,
    });
  };
  const [filterValue, setFilterVale] = useState(
    disableInheritance ? defaultValue : (isOperate ? filterExtend : defaultValue)
  );
  const [resetBaseValue, setResetBaseValue] = useState(() => buildInitialResetBaseValue(defaultValue));

  const { isNewConfirm, setIsNewConfirm, isNewCheckGame, setIsNewCheckGame, operatedGame, setOperatedGame } =
    useOperationState();

  /**
   * @description 榜单核心逻辑hooks，该hooks内处理了，页面初始化查询、排序、翻页等关联逻辑
   */
  const {
    isNodata,
    fetchParams,
    chartType,
    setChartType,
    visualisLoading,
    setVisualisLoading,
    barOrPieData,
    setBarOrPieData,
    lineData,
    tableLoading,
    setTableLoading,
    tableTotal,
    tableData,
    tableSelectionDefault,
    tableSelection,
    setTableSelection,
    gameDetailsLoading,
    gameDetailsMapping,
    dates,
    getGameData,
    getLineChartData,
    handleClearSearch,
  } = useTopChartsCore({
    metrics,
    hasUrlShortLink: filterCfg?.hasUrlShortLink,
    defaultSelectGameNum: getValidTopSelectGame(topSelect as any),
    shortLinkValidQuery,
    defaultParams,
    filterValue,
    setFilterVale,
    /**
     * @description 获取折线图数据成功后，重置isNewConfirm、isNewCheckGame为false
     */
    onGetLineChartDataSuccess: () => {
      setIsNewConfirm(false);
      setIsNewCheckGame(false);
    },
    /**
     * @description 没继承到游戏时，弹窗通知用户
     */
    onNoGameExtend: (gameListDefault: any[], tableSelectionDefault) => {
      Modal.error({
        size: 'm',
        message: 'Notice',
        description: (
          <>
            <p>{formatMessage({ id: 'dbi_gameselected_err_info' })} </p>
            <p>{formatMessage({ id: 'dbi_gameselected_reset_info' })}</p>
          </>
        ),
        buttons: [
          <Button
            key="btn"
            type="primary"
            onClick={(e) => {
              e?.stopPropagation();
              setBarOrPieData(gameListDefault);
              setTableSelection(tableSelectionDefault);
              /** 处理游戏继承 */
              if (topChartsType !== MultiDataSourceTopChartsPageType.DataBrainCalibration) {
                const idList = gameListDefault?.map((game) => game?.game_id) || [];
                dispatch({ type: `${DBI_TOPCHARTS}/setActiveUserGameList`, payload: idList });
              }
            }}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>,
        ],
      });
    },
  });

  /**
   * @description 榜单可视化卡片指标、排序指标相关的文案
   */
  const { titleNow, orderNameInfo } = useTextInfo({
    topChartsType,
    fetchParams,
  });

  useCheckedGenresClear(fetchParams);

  // 异动指标选项 - 根据日期粒度动态生成
  const surgeMetricOptions = useMemo(() => {
    const dateType = fetchParams?.date_type || 'daily';
    return getSurgeMetricOptions(dateType);
  }, [fetchParams?.date_type]);

  // Filter 组件的 ref，用于从外部调用筛选器方法
  const filterRef = useRef<ICommonFilterInstance | null>(null);

  /**
   * HotTagsSource 选项变化回调
   * 将选中 option 的 value（与 filter_tag_name_details 格式相同）
   * 通过 parseUrlFilterParams 解析为 filterValue，再调用 filterRef.setValueAndConfirm 应用
   */
  const [selectedHotTagOption, setSelectedHotTagOption] = useState<any>(
    filterCfg?.specialParams?.hotTagDefaultOption ?? null,
  );
  const resetBaseValueRef = useRef(resetBaseValue);
  resetBaseValueRef.current = resetBaseValue;

  useEffect(() => {
    setResetBaseValue((prevValue: any) => buildInitialResetBaseValue(prevValue));
  }, [defaultValue, filterCfg?.filterKeyList, rangeTimeComponentDefault]);

  // hotTagDefaultOption 由 Page 层异步计算，初始为 null，等 hotTagsOptions 加载后才有值
  // 需要监听它的变化来同步初始选中状态
  useEffect(() => {
    const defaultOption = filterCfg?.specialParams?.hotTagDefaultOption;
    if (defaultOption) {
      setSelectedHotTagOption(defaultOption);
    }
  }, [filterCfg?.specialParams?.hotTagDefaultOption]);

  // 使用 ref 保存最新的 genreOptionsMap，避免 useCallback 闭包陈旧引用
  const genreOptionsMapRef = useRef(genreOptionsMap);
  genreOptionsMapRef.current = genreOptionsMap;

  const handleHotTagConfirm = React.useCallback(async (option: any) => {
    if (!option?.value) return;
    setSelectedHotTagOption(option);
    try {
      const parsedFilterValue = await parseUrlFilterParams(option.value, { genreOptionsMap: genreOptionsMapRef.current });
      if (parsedFilterValue && Object.keys(parsedFilterValue).length > 0) {
        const nextFilterValue = {
          ...buildInitialResetBaseValue(resetBaseValueRef.current || defaultValue),
          ...parsedFilterValue,
          [filterKeyMap.HOT_TAGS_SOURCE]: { value: option },
        };
        setFilterVale(nextFilterValue);
      }
    } catch (error) {
      console.error('[handleHotTagConfirm] parseUrlFilterParams failed:', error);
    }
  }, [defaultValue]);

  // 将 onHotTagConfirm 注入 filterCfg.specialParams，供 CommonFilter → HotTagsSource 使用
  const filterCfgWithHotTag = React.useMemo(() => ({
    ...filterCfg,
    specialParams: {
      ...filterCfg?.specialParams,
      myFilterStyle: { width: 140 },
      regionClassName: 'hot-tags-region-select',
      genreClassName: 'hot-tags-genre-select',
      onHotTagConfirm: handleHotTagConfirm,
    },
  }), [filterCfg, handleHotTagConfirm]);

  const getData = (
    args: {
      params: any;
      isCheckGame: boolean;
      isSearch?: boolean;
      isUpdateLineChart: boolean;
      updateFilterValues?: any;
      myFilterValue?: string;
    },
    idList?: string[],
  ) => {
    const {
      params,
      isCheckGame,
      isSearch = false,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      myFilterValue = filterSaveName,
    } = args;

    getGameData(
      {
        params: {
          ...params,
          game_code: isSearch ? params?.game_code : [],
          topcharts_name: topchartsNameParams,
        },
        isCheckOrSearch: isCheckGame,
        isUpdateLineChart,
        updateFilterValues,
        filterSaveName: myFilterValue,
      },
      idList,
    )
      .then(() => {
        setTableLoading(false);
        setVisualisLoading(false);
      })
      .catch(() => {
        setTableLoading(false);
        setVisualisLoading(false);
        setIsError(true);
      });
  };

  const getTrend = async (params: any, idList: string[]) => {
    try {
      setVisualisLoading(true);
      await getLineChartData(params, idList);
    } catch (error) {
      setIsError(true);
    } finally {
      setVisualisLoading(false);
    }
  };

  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    if (value === ChartType.Line && tableSelection?.length && isNewConfirm && isNewCheckGame) {
      const idList = tableSelection.map((t) => t?.gameId);
      getTrend(fetchParams, idList);
    }
    setChartType(value);
  };

  const getTableSearchList = (id: string) => {
    setTableLoading(true);
    getData({
      params: { ...fetchParams, game_code: [id] },
      isCheckGame: true,
      isSearch: true,
      isUpdateLineChart: false,
    });
  };

  // 表格列表项选择事件回调
  const handleTableCheckGame = (selectList: TableSelectionType[], checkStatus: TableSelectStatusType) => {
    setIsNewConfirm(true);
    setIsNewCheckGame(true);

    const idList = selectList.map((t) => t?.gameId);
    if (chartType === ChartType.Line) {
      getTrend(fetchParams, idList);
    }
    /** 是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
      // GUARD: 当继承被禁用时，跳过Redux dispatch以避免污染共享状态
      if (!disableInheritance) {
        dispatch({ type: `${DBI_TOPCHARTS}/setActiveUserGameList`, payload: [] });
      }
    } else {
      setOperatedGame(true);
      /** 处理游戏继承 */
      // GUARD: 当继承被禁用时，跳过Redux dispatch以避免污染共享状态
      if (!disableInheritance && topChartsType !== MultiDataSourceTopChartsPageType.DataBrainCalibration) {
        dispatch({
          type: `${DBI_TOPCHARTS}/setActiveUserGameList`,
          payload: idList,
        });
      }
    }
    setBarOrPieData(selectList);
    setTableSelection(selectList);
  };

  const handleTableSortChange = (e: SortByItemType) => {
    const newFetchParams = {
      ...fetchParams,
      order: e?.sortVal,
      order_metric: e?.sortMetric,
      order_source: e?.orderSource,
      ratio_type: e?.ratioType,
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    setTableLoading(true);
    if (!operatedGame) {
      /** 没有勾选时点排序则重新Loading柱状图数据 */
      setVisualisLoading(true);
    }
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: !operatedGame,
    });
  };

  const handleTablePagingChange = (e: PagingQuery) => {
    const newFetchParams = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      // page_size: e?.pageSize || 100,
      page_size: e?.pageSize || 50,
    };
    setTableLoading(true);
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: false,
    });
  };

  const handleConfirm = async (value: any, filter: any, myFilterValue: any) => {
    // 操作后Url不再影响筛选器
    const newFormValue = {
      ...fetchParams,
      ...value,
      page: 1,
      page_size: 50,
    };

    // 检查异动指标是否与新粒度匹配
    const auMetrics = ['dau', 'wau', 'mau'];
    const newDateType: IGranularity = value?.date_type || fetchParams?.date_type;
    if (surgeMetric && auMetrics.includes(surgeMetric)) {
      const expectedSurgeMetric = getGranularityAUMetricKey(newDateType);

      if (surgeMetric !== expectedSurgeMetric) {
        // 粒度不匹配，清除异动指标
        setSurgeMetric(expectedSurgeMetric);
        newFormValue.surge_metric = expectedSurgeMetric;
      }
    }

    // 请求接口
    setTableLoading(true);
    setVisualisLoading(true);
    // 修改筛选器后，游戏应该重置为未勾选状态
    setOperatedGame(false);
    dispatch({ type: 'sizer/setTopTopMobileGamecode', payload: [] });

    getData({
      params: newFormValue,
      isCheckGame: false,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: filter,
      myFilterValue,
    });

    setIsNewConfirm(true);
    setIsNewCheckGame(true);
  };

  const handleReset = () => {
    setOperatedGame(false);
    // GUARD: 当继承被禁用时，跳过Redux dispatch以避免污染共享状态
    if (!disableInheritance) {
      dispatch({ type: `${DBI_TOPCHARTS}/setActiveUserGameList`, payload: [] });
    }
  };

  /**
   * @description 异动指标变更处理
   * 选择异动指标后，调整排序参数并重新请求数据
   */
  const handleSurgeMetricChange = (value: string) => {
    setSurgeMetric(value);

    // 构建新的请求参数
    const newFetchParams = {
      ...fetchParams,
      order_metric: 'dau', // 默认排序指标为AU指标
      order: SortType.Desc, // 默认排序方式为降序
      ratio_type: 0, // 默认排序模式为数值
      surge_metric: value,
      page: 1,
    };

    // 如果选择了异动指标，需要调整排序参数
    if (value) {
      newFetchParams.order_metric = value; // 异动指标对应的排序指标
      newFetchParams.order = SortType.Desc; // 异动指标固定降序，页面排序指标也默认调整为降序
      newFetchParams.ratio_type = 1; // 异动指标固定按环比降序，页面排序指标也默认调整为按环比降序
    }

    // 重新请求数据
    setTableLoading(true);
    if (!operatedGame) {
      setVisualisLoading(true);
    }
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: !operatedGame,
    });
  };

  /**
   * App Magic 品类标签点击回调
   * 点击标签后将该品类添加到筛选器中并重新请求数据
   * @param type 点击的品类类型：SuperGenre(一级品类) | MajorGenre(二级品类) | MidGenre(三级品类) | SubGenre(四级品类)
   * @param superTag 一级品类值
   * @param majorTag 二级品类值（可选，当 type 为 MajorGenre、MidGenre 或 SubGenre 时有值）
   * @param midTag 三级品类值（可选，当 type 为 MidGenre 或 SubGenre 时有值）
   * @param subTag 四级品类值（可选，仅当 type 为 SubGenre 时有值）
   */
  const handleAppMagicTagClick = (
    type: AppMagicGenreTagType,
    superTag: string,
    majorTag?: string,
    midTag?: string,
    subTag?: string,
  ) => {
    // 获取 App Magic 品类 options 数据
    const appMagicOptions = filterCfg?.specialParams?.genreOptions?.app_magic;
    const genreOptionsLoading = filterCfg?.specialParams?.genreOptionsLoading;

    // 如果品类数据正在加载或不存在，则不处理
    if (genreOptionsLoading || !appMagicOptions || appMagicOptions.length === 0) {
      return;
    }

    // 构建新的品类筛选器值
    const newGenreValue = generateGenreSelectValueSimple(
      genreOptionsMap,
      'appmagic',
      superTag,
      majorTag,
      midTag,
      subTag,
    );
    if (!newGenreValue) {
      return;
    }

    // 根据模式决定最终的品类值
    let finalGenreValue;
    if (appMagicTagClickMode === 'append') {
      // 追加模式：合并当前已选品类和新品类
      const currentGenreValue = filterValue?.[filterKeyMap.GENRETOR];
      finalGenreValue = mergeAppMagicGenreValue(currentGenreValue, newGenreValue, appMagicOptions);
    } else {
      // 替换模式（默认）：直接使用新品类值
      finalGenreValue = newGenreValue;
    }

    // 通过 ref 调用筛选器的 setValueAndConfirm 方法
    // 这样可以确保筛选器 UI 状态和 API 请求参数都正确更新
    filterRef.current?.setValueAndConfirm({
      [filterKeyMap.GENRETOR]: finalGenreValue,
    });
  };

  const handleStAppMagicTagClick = React.useCallback(
    buildTopChartsTagClickHandler({
      filterRef,
      filterValue,
      setFilterValue: setFilterVale,
      sensorTowerTagConfig: filterCfg?.specialParams?.sensorTowerTagConfig,
    }),
    [filterCfg?.specialParams?.sensorTowerTagConfig, filterValue],
  );

  /**
   * @description 表格中 Tag 点击事件，将点击的 tag 赋值给对应的筛选器并触发查询
   */
  const handleTagClick = (type: string, tag: string) => {
    if (
      filterKeyMap.SENSOR_TOWER_TAG_KEYS.includes(type) ||
      filterKeyMap.APPMAGIC_TAG_KEYS.includes(type)
    ) {
      handleStAppMagicTagClick(type, tag);
      return;
    }

    // 将点击的 tag 给对应的筛选器赋值
    const currentFilterValue = { ...filterValue, [type]: { value: [tag] } };
    setFilterVale(currentFilterValue);

    // 发起请求
    const newFormValue = {
      ...fetchParams,
      [type]: [tag],
      page: 1,
      page_size: 50,
    };

    setTableLoading(true);
    setVisualisLoading(true);

    getData({
      params: newFormValue,
      isCheckGame: false,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: currentFilterValue,
    });
  };

  useEffect(() => {
    if (filterLoading || !filterCfg?.hasUrlShortLink) return;
    updateShortLinkPath({
      shortLinkQuery: {
        [URL_CHART_TYPE]: chartType,
        [URL_SOURCE_NAME]: fetchParams.order_source,
        [URL_SORT_NAME]: fetchParams.order_metric,
        [URL_RATIO_TYPE]: fetchParams.ratio_type,
        [URL_ORDER]: fetchParams.order,
      },
    });
  }, [
    filterLoading,
    chartType,
    fetchParams.order_source,
    fetchParams.order_metric,
    fetchParams.ratio_type,
    fetchParams.order,
  ]);

  // 组件卸载时的清理逻辑
  // 当 disableInheritance 为 true 时，确保离开页面时不会残留任何状态
  useEffect(() => {
    return () => {
      if (disableInheritance) {
        // 重置游戏选择列表，防止状态泄漏到其他页面
        dispatch({ type: `${DBI_TOPCHARTS}/setActiveUserGameList`, payload: [] });
      }
    };
  }, [disableInheritance, dispatch]);

  useEffect(() => {
    setTableLoading(true);
    setVisualisLoading(true);
    // 拿缓存值需要拿到用户id
    if (filterLoading || !userInfo?.userid) return;

    // Check for navigation from GameInfoTagCard via URL query params
    const query = history.location.query || {};
    let overrideFilterExtend = null;
    let overrideIsOperate = isOperate;
    let overrideFilterName = filterSaveName;

    if (query?.fromGameInfoTag === 'true') {
      const tagType = query.tagType as string;
      const tagValue = query.tagValue as string;

      // Reconstruct the logic from GameInfoTagCard
      const resetTypes = [
        filterKeyMap.STEAM_TAGS,
        filterKeyMap.GAME_MODE,
        filterKeyMap.GAME_ENGINE,
      ].filter((key) => key !== tagType);

      const resetFilters = resetTypes.reduce((acc, key) => {
        acc[key] = { value: [] };
        return acc;
      }, {} as unknown as { [key: string]: { value: string[] } });

      overrideFilterExtend = {
        ...(filterExtend || defaultValue || {}),
        ...resetFilters,
        [tagType]: {
          value: [tagValue],
          ...(tagType === filterKeyMap.GAME_ENGINE ? { logic: 'OR' } : {}),
        },
      };

      // Force isOperate to true
      overrideIsOperate = true;
      // Force filterName to null (we don't want saved filters to override)
      overrideFilterName = null;

      // Update Redux state immediately so subsequent renders are correct
      setFilterExtend(overrideFilterExtend);

      // Clear the URL params to prevent re-triggering on refresh
      history.replace(history.location.pathname);
    }

    let newFetchParams = { ...fetchParams };

    // 当 disableInheritance 为 true 时，禁用跨Tab继承
    // 仅应用：defaultValue + tab特定后端配置(shortLinkFilterValue) + URL参数
    // 不应用：Redux filterExtend(跨Tab继承) + localStorage保存的筛选器
    if (disableInheritance) {
      const { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
        isOperate: false, // 禁用Redux跨Tab继承
        userInfo,
        urlCustomParams,
        urlShortLinkFilter: shortLinkFilterValue, // 保留：Tab特定的后端配置
        filterSaveData: {}, // 跳过：localStorage保存的筛选器
        filterExtend: {}, // 跳过：Redux跨Tab状态
        specialParams: filterCfg?.specialParams,
        filterName: filterSaveName,
        filterKeyList: filterCfg?.filterKeyList ?? Object.keys(defaultValue),
      });

      const newFilterValuesTemp = { ...defaultValue, ...newFilterValues };

      // 注意：不调用 setFilterExtend 以避免污染共享Redux状态
      setFilterVale(newFilterValuesTemp);
      setResetBaseValue(buildInitialResetBaseValue(newFilterValuesTemp));

      newFetchParams = {
        ...newFetchParams,
        ...urlCustomParams,
        ...filterCfg?.handleFilterToApiParams?.({ ...newFetchParams, ...urlCustomParams, ...filterParams }),
      };

      getData({
        params: newFetchParams,
        isCheckGame: false, // 无游戏继承
        isSearch: false,
        isUpdateLineChart: true,
        updateFilterValues: newFilterValuesTemp,
      });
      return; // 跳过正常继承流程
    }

    // 正常继承流程 (disableInheritance === false 或 undefined)
    const { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
      isOperate: overrideIsOperate,
      userInfo,
      urlCustomParams,
      urlShortLinkFilter: shortLinkFilterValue,
      filterSaveData: filterSaveData?.apiValue,
      filterExtend: overrideFilterExtend || filterExtend,
      specialParams: filterCfg?.specialParams,
      filterName: overrideFilterName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(defaultValue),
      genreOptionsMap,
    });

    const newFilterValuesTemp = { ...defaultValue, ...newFilterValues };

    // 如果有短链接，设置为继承模式
    if (overrideIsOperate || hasUrlShortLinkVal) {
      // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
      setFilterExtend(newFilterValuesTemp);
    }
    setFilterVale(newFilterValuesTemp);
    setResetBaseValue(buildInitialResetBaseValue(newFilterValuesTemp));

    newFetchParams = {
      ...newFetchParams,
      ...urlCustomParams,
      ...filterCfg?.handleFilterToApiParams?.({ ...newFetchParams, ...urlCustomParams, ...filterParams }),
    };

    if (topChartsType !== MultiDataSourceTopChartsPageType.DataBrainCalibration && extendGameList?.length > 0) {
      // 游戏继承
      getData(
        {
          params: newFetchParams,
          isCheckGame: true,
          isSearch: false,
          isUpdateLineChart: true,
          updateFilterValues: newFilterValuesTemp,
        },
        extendGameList,
      );

      setOperatedGame(true);
    } else {
      getData({
        params: newFetchParams,
        isCheckGame: operatedGame,
        isSearch: false,
        isUpdateLineChart: true,
        updateFilterValues: newFilterValuesTemp,
      });
    }
  }, [userInfo, filterLoading, shortLinkFilterValue, disableInheritance]);

  return (
    <div style={style} className={classnames('dbi-multi-datasource-top-charts-template', className)}>
      <MarqueeBanner />
      <Card>
        <Card.Body>
          <Filter
            ref={filterRef}
            buriedPointParams={pageLogInfo}
            maxReleaseTime={useMemo(() => moment().format('YYYY-MM-DD'), [])}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            filterCfg={filterCfgWithHotTag}
            defaultValue={defaultValue}
            resetBaseValue={resetBaseValue}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            filterConfirm={handleConfirm}
            filterReset={handleReset}
            extendPageId={pageId}
          />
        </Card.Body>
      </Card>
      <Card className="dbi-mobileGames-card-context">
        <Card.Body
          operation={tableLoading || <ChartIconType chartType={chartType} handleChartType={handleChartType} />}
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: 5 }}>{titleNow}</span>
              <TipContext
                AWidth={600}
                tipText={formatMessage({
                  id: `dbi_info_following_mobile_title`,
                })}
              />
              <span style={{ marginRight: 5 }}></span>
              <ArrowDownText isNew order={fetchParams?.order} sortedData={orderNameInfo} />
            </div>
          }
        >
          {visualisLoading ? (
            <div style={{ height: 350 }}>
              <Loading />
            </div>
          ) : (
            <MultiDataSourceTopChartsVisualis
              isDataBrain={isDataBrain}
              entityType={entityType}
              imageType={entityType}
              chartType={chartType}
              topChartsType={topChartsType}
              primaryKey={GAME_PRIMARY_KEY}
              dateType={fetchParams?.date_type}
              dataSources={fetchParams?.data_sources}
              sourceOrders={fetchParams?.data_sources}
              orderTypeInfo={fetchParams?.order}
              fetchParams={fetchParams}
              titleNow={titleNow}
              isVisualisLoading={visualisLoading}
              isError={isError}
              isNodata={isNodata}
              barOrPieData={barOrPieData}
              lineData={lineData}
              metrics={metrics}
              metric={metric}
              dates={dates}
              gameHighId={urlDefaultSelectedGameId}
              isStudio={Boolean(urlDefaultSelectedGameId)}
            />
          )}
        </Card.Body>
      </Card>
      <Justify
        className="dbi-card-justify"
        left={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ChangedMetricSelect
              placeholder="dbi_surge_metric_placeholder"
              pageLogKey={pageLogKey}
              options={surgeMetricOptions}
              value={surgeMetric}
              onChange={handleSurgeMetricChange}
            />
            <TipContext AWidth={500} tipText={formatMessage({ id: 'dbi_surge_metric_tooltip_mobile' })} />
          </div>
        }
        right={
          <>
            <SearchRecent
              pageLogInfo={pageLogInfo}
              searchType={entityType}
              fetchParams={convertParams(metrics, { ...fetchParams, page: 1, filter_ids: [] })}
              initSearchInput={!(fetchParams?.game_code?.length > 0)}
              searchListApi={fetchMobileTopChartsDataWhenSearchKeyword}
              getTableResultList={getTableSearchList}
              transTableDataForSearch={transTableDataForSearch}
              handleClear={handleClearSearch}
            />
            {/* databrain页下载不提示 */}
            <ExportTable
              useWebDownload
              isGameTopCharts
              isNeedCheckedLines
              entityType={entityType}
              downloadModalSize={820}
              newStyleFileName={(() => {
                const topChartsTypeToBaseName: Record<string, string> = {
                  activeuser: 'Top Charts_Mobile Games_Active Users',
                  revenue: 'Top Charts_Mobile Games_Revenue',
                  downloads: 'Top Charts_Mobile Games_Downloads',
                  databrain: 'Top Charts_Mobile Games_DataBrain Calibration',
                };
                const base = topChartsTypeToBaseName[topChartsType] || 'Top Charts_Mobile Games_Active Users';
                if (!selectedHotTagOption) return base;
                const hotTagName = locale === 'zh-CN'
                  ? (selectedHotTagOption.name_cn || selectedHotTagOption.name || '')
                  : (selectedHotTagOption.name_en || selectedHotTagOption.name || '');
                return hotTagName ? `Hot Tags_${hotTagName}_${base}` : base;
              })()}
              downloadLogConfigKey={downloadLogKey}
              businessType={`mobile-${topChartsType}`}
              downloadKey="mobileTopChartsMultiDataSource"
              downloadQuery={{ isShowMaxDownloadRowsTips: 'true' }}
              stickText={showNewText ? formatMessage({ id: 'dbi_mobile_download_modal_stick_tips' }) : undefined}
              hasBubbleCloseIcon={showFirstDownLoadBubble}
              isDisableDownload={tableLoading}
              total={tableTotal}
              tableParams={{
                ...fetchParams,
                ...PAGINATION_FETCH_PARAMS,
                metrics,
                select_ids: tableSelection,
                regionOptions: filterCfg?.specialParams?.regionOptions,
              }}
              defaultCheckedLines={operatedGame}
              onBubbleClose={() => {
                setShowFirstDownLoadBubble?.(false);
                // 下载首次提示关闭打点
                buriedBtnPoint(dbiBtnLogMes.topchartMobileGameActiveUserDownLoadTip);
              }}
              getSortedGameIds={async (gameIds?: string[] | null) => {
                if (!Array.isArray(gameIds) || gameIds.length === 0) {
                  return [];
                }

                const fetchParamsTmp = convertParams(metrics, {
                  ...fetchParams,
                  ...PAGINATION_FETCH_PARAMS,
                  page: 1,
                  filter_ids: gameIds,
                });
                const res = await fetchMetricDataGames(fetchParamsTmp);
                const { list } = res?.data || {};

                if (!Array.isArray(list) || list.length === 0) {
                  return [];
                }

                return list.map((item) => item?.game_id);
              }}
            />
          </>
        }
      />
      <CardSlot>
        <MultiDataSourceTopChartsTable
          entityType={entityType}
          topChartsType={topChartsType}
          showFirstGuide={showFirstGuide}
          selectDataSources={fetchParams?.data_sources}
          genreSource={fetchParams?.genre_source}
          genreOptionsMap={genreOptionsMap}
          dateType={fetchParams?.date_type}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          loading={tableLoading}
          records={tableData}
          tableTotal={tableTotal}
          tableSelection={tableSelection}
          tableSelectionAll={tableSelectionDefault}
          gameDetailsLoading={gameDetailsLoading}
          gameDetailsMapping={gameDetailsMapping}
          onTableCheckGame={handleTableCheckGame}
          onTableSortChage={handleTableSortChange}
          onTablePagingChange={handleTablePagingChange}
          onTagClick={handleTagClick}
          onAppMagicTagClick={handleAppMagicTagClick}
          filterValue={filterValue}
          pageSortAttr={{
            tablePageIndex: fetchParams?.page,
            tablePageSize: fetchParams?.page_size,
            currentSortValue: fetchParams?.order,
            currentSortMetric: AU_METRICS.includes(fetchParams?.order_metric) ? 'dau' : fetchParams?.order_metric,
            currentOrderSource: fetchParams?.order_source,
            currentRatioType: fetchParams?.ratio_type,
          }}
        />
      </CardSlot>
    </div>
  );
};

export default MultiDataSourceTopChartsTemplate;
